import { Empty, Input, InputNumber, Select, Space, Switch, Typography, ColorPicker, Divider } from 'antd';
import type { FlyerFieldConfig } from '@/types/flyer';
import { PROFILE_AUTOFILL_SOURCES, TOPIC_AUTOFILL_SOURCES } from '@/types/flyer';
import { clampRect, fieldToRect, rectToField } from '@/lib/flyer-canvas';

interface Props {
  field: FlyerFieldConfig | null;
  programBound: boolean;
  onChange: (f: FlyerFieldConfig) => void;
}

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>{label}</Typography.Text>
    {children}
  </Space>
);

export function FieldInspector({ field, programBound, onChange }: Props) {
  if (!field) return <Empty description="Select a field" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  const f = field;
  const setStyle = (patch: Record<string, unknown>) => onChange({ ...f, style_config: { ...f.style_config, ...patch } });
  const setRectPart = (part: Partial<{ x: number; y: number; w: number; h: number }>) =>
    onChange(rectToField(f, clampRect({ ...fieldToRect(f), ...part })));
  const pctInput = (value: number, key: 'x' | 'y' | 'w' | 'h') => (
    <InputNumber size="small" min={0} max={100} step={0.1} precision={1} value={Math.round(value * 1000) / 10}
      onChange={(v) => setRectPart({ [key]: (v ?? 0) / 100 })} style={{ width: 90 }} addonAfter="%" />
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={8}>
      <Row label="Label"><Input size="small" value={f.label} onChange={(e) => onChange({ ...f, label: e.target.value })} style={{ width: 170 }} /></Row>
      <Row label="X">{pctInput(f.x_percent, 'x')}</Row>
      <Row label="Y">{pctInput(f.y_percent, 'y')}</Row>
      <Row label="Width">{pctInput(f.width_percent, 'w')}</Row>
      <Row label="Height">{pctInput(f.height_percent, 'h')}</Row>
      <Divider style={{ margin: '8px 0' }} />
      <Row label="Font size"><InputNumber size="small" min={1} value={f.style_config.fontSize ?? 100} onChange={(v) => setStyle({ fontSize: v ?? 100 })} style={{ width: 90 }} /></Row>
      <Row label="Color">
        <ColorPicker size="small" value={(f.style_config.color as string) ?? '#000000'}
          onChange={(c) => setStyle({ color: c.toHexString() })} />
      </Row>
      <Row label="Align">
        <Select size="small" value={f.style_config.alignment ?? 'center'} style={{ width: 100 }}
          options={['left', 'center', 'right'].map((a) => ({ value: a, label: a }))}
          onChange={(v) => setStyle({ alignment: v })} />
      </Row>
      <Row label="Bold"><Switch size="small" checked={f.style_config.fontWeight === 'bold'} onChange={(v) => setStyle({ fontWeight: v ? 'bold' : 'regular' })} /></Row>
      <Row label="Italic"><Switch size="small" checked={!!f.style_config.isItalic} onChange={(v) => setStyle({ isItalic: v })} /></Row>
      <Row label="Letter spacing"><InputNumber size="small" value={f.style_config.letterSpacing ?? 0} onChange={(v) => setStyle({ letterSpacing: v ?? 0 })} style={{ width: 90 }} /></Row>
      <Divider style={{ margin: '8px 0' }} />
      <Row label="Auto-fill"><Switch size="small" checked={!!f.auto_fill_enabled} onChange={(v) => onChange({ ...f, auto_fill_enabled: v })} /></Row>
      <Select
        size="small" style={{ width: '100%' }} placeholder="Auto-fill source" allowClear
        value={f.auto_fill_source} disabled={!f.auto_fill_enabled}
        onChange={(v) => onChange({ ...f, auto_fill_source: v ?? undefined })}
        options={[
          { label: 'Profile', options: PROFILE_AUTOFILL_SOURCES.map((s) => ({ value: s, label: s.replace('profile.mlo_info.', 'MLO ') })) },
          {
            label: programBound ? 'Program' : 'Program (bind a program first)',
            options: TOPIC_AUTOFILL_SOURCES.map((s) => ({ value: s, label: s.replace('topic.', 'program '), disabled: !programBound })),
          },
        ]}
      />
    </Space>
  );
}
